import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatVndWithUnit } from "../../../shared/format";
import type {
  DiscountType,
  InvoiceItemInput,
  VatCategory,
} from "../../../shared/types/billing";

function vatRate(cat: VatCategory): number {
  switch (cat) {
    case "VAT_5": return 0.05;
    case "VAT_8": return 0.08;
    case "VAT_10": return 0.10;
    default: return 0;
  }
}

function lineGross(it: InvoiceItemInput): number {
  return it.quantity * it.unitPrice;
}

function lineDiscountAmt(it: InvoiceItemInput): number {
  if (!it.discountType || it.discountValue == null) return 0;
  if (it.discountType === "PERCENT") {
    return Math.round(lineGross(it) * Math.max(0, Math.min(100, it.discountValue)) / 100);
  }
  return Math.max(0, Math.min(lineGross(it), it.discountValue));
}

function lineTotal(it: InvoiceItemInput): number {
  return Math.round(lineGross(it) - lineDiscountAmt(it));
}

export function computeTotals(
  items: InvoiceItemInput[],
  invDiscountType: DiscountType | null,
  invDiscountValue: number | null
) {
  const subtotal = items.reduce((s, it) => s + lineTotal(it), 0);
  let invoiceDiscount = 0;
  if (invDiscountType && invDiscountValue != null) {
    if (invDiscountType === "PERCENT") {
      invoiceDiscount = Math.round(subtotal * Math.max(0, Math.min(100, invDiscountValue)) / 100);
    } else {
      invoiceDiscount = Math.max(0, Math.min(subtotal, invDiscountValue));
    }
  }
  const afterDiscount = subtotal - invoiceDiscount;
  const vatTotal = items.reduce((s, it) => {
    const factor = subtotal > 0 ? lineTotal(it) / subtotal : 0;
    const lineAfter = lineTotal(it) - Math.round(invoiceDiscount * factor);
    return s + Math.round(lineAfter * vatRate(it.vatCategory));
  }, 0);
  const grandTotal = Math.round(afterDiscount + vatTotal);
  return { subtotal, invoiceDiscount, vatTotal, grandTotal };
}

interface Props {
  items: InvoiceItemInput[];
  discountType: DiscountType | null;
  discountValue: number | null;
  onDiscountChange: (t: DiscountType | null, v: number | null) => void;
}

export function InvoiceTotals({ items, discountType, discountValue, onDiscountChange }: Props) {
  const { subtotal, invoiceDiscount, vatTotal, grandTotal } = computeTotals(
    items,
    discountType,
    discountValue
  );

  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className="text-muted-foreground">Tạm tính</span>
        <span className="font-mono tabular-nums">{formatVndWithUnit(subtotal)}</span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <span className="text-muted-foreground">Giảm giá HĐ</span>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            value={discountValue ?? ""}
            placeholder="0"
            className="h-8 w-24 text-right font-mono tabular-nums"
            onChange={(e) => {
              const v = e.target.value === "" ? null : Math.max(0, Number(e.target.value));
              onDiscountChange(discountType ?? "AMOUNT", v);
            }}
          />
          <Select
            value={discountType ?? "_"}
            onValueChange={(next) => {
              if (next === "_") onDiscountChange(null, null);
              else onDiscountChange(next as DiscountType, discountValue);
            }}
          >
            <SelectTrigger className="h-8 w-16">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_">—</SelectItem>
              <SelectItem value="PERCENT">%</SelectItem>
              <SelectItem value="AMOUNT">đ</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <span
          className="font-mono tabular-nums"
          style={{ color: "#d97706" }}
        >
          −{formatVndWithUnit(invoiceDiscount)}
        </span>
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-muted-foreground">VAT</span>
        <span className="font-mono tabular-nums">{formatVndWithUnit(vatTotal)}</span>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-border pt-2 text-base font-semibold">
        <span>Tổng cộng</span>
        <span className="font-mono tabular-nums">{formatVndWithUnit(grandTotal)}</span>
      </div>
    </div>
  );
}
